import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  BackArrow,
  Button,
  Input,
  Select,
  TopProgress,
  Typography,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { zonesService, clientService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { usePageTitle } from "../../../../context/navigationContext";
import {
  Container,
  Header,
  FormSection,
  FormActions,
  RadioGroup,
  RadioOption,
  RadioLabel,
  PriceInfo,
} from "./styles";

type CreateOptions = {
  regions: Array<{
    id: string;
    name: string;
  }>;
  selected_region: string;
  types: Array<[string, string]>;
};

const ZoneCreate: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  usePageTitle(t("CREATE_DNS_ZONE"));

  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [createOptions, setCreateOptions] = useState<CreateOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [createInClientProject, setCreateInClientProject] = useState<boolean>(false);
  const [clients, setClients] = useState<any[]>([]);
  const [clientSearchString, setClientSearchString] = useState<string>("");
  const [clientsLoading, setClientsLoading] = useState<boolean>(false);
  const [estimatedPrice, setEstimatedPrice] = useState<string>("");
  const [priceLoading, setPriceLoading] = useState<boolean>(false);

  useEffect(() => {
    setOptionsLoading(true);
    zonesService
      .getCreateOptions()
      .then((res) => {
        setCreateOptions(res.data as CreateOptions);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_CREATE_OPTIONS");
        viewAlert({
          severity: "error",
          message,
        });
        navigate("/openstack/zones");
      })
      .finally(() => {
        setOptionsLoading(false);
      });
  }, [t, viewAlert, navigate]);

  // Load clients with search
  useEffect(() => {
    if (createInClientProject) {
      setClientsLoading(true);
      const timer = setTimeout(() => {
        clientService
          .getAll({
            page: 1,
            page_size: 100,
            search: clientSearchString,
            has_enabled_project: true
          })
          .then((res) => {
            const clientsData = res.data?.objects || [];
            setClients(clientsData);
          })
          .catch((error) => {
            console.error("Failed to load clients", error);
          })
          .finally(() => {
            setClientsLoading(false);
          });
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [createInClientProject, clientSearchString]);

  const typeOptions = useMemo(() => {
    if (!createOptions) {
      return [];
    }
    return (createOptions.types || []).map(([value, label]) => ({
      id: value,
      value,
      label,
    }));
  }, [createOptions]);

  const clientOptions = useMemo(() => {
    return clients.map((client) => ({
      id: client.id,
      value: client.id,
      label: `${client.first_name} ${client.last_name} (${client.email})`,
    }));
  }, [clients]);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t("REQUIRED_FIELD")),
        email: yup.string().email(t("INVALID_EMAIL")).required(t("REQUIRED_FIELD")),
        ttl: yup.number().min(0, t("TTL_MIN_VALUE")).required(t("REQUIRED_FIELD")),
        type_: yup.string().required(t("REQUIRED_FIELD")),
        client: createInClientProject
          ? yup.string().required(t("REQUIRED_FIELD"))
          : yup.string().nullable(),
        description: yup.string().nullable(),
        ip_address: yup.string().nullable(),
      }),
    [t, createInClientProject]
  );

  const formik = useFormik({
    enableReinitialize: false,
    validationSchema,
    initialValues: {
      name: "",
      email: "",
      ttl: 1800,
      type_: "PRIMARY",
      description: "",
      ip_address: "",
      client: "",
    },
    onSubmit: (formValues) => {
      setCreateLoading(true);
      setPageLoading(true);
      const payload: any = {
        name: formValues.name,
        email: formValues.email,
        ttl: formValues.ttl,
        type_: formValues.type_,
        description: formValues.description || "",
        ip_address: formValues.ip_address || "",
      };

      if (createInClientProject && formValues.client) {
        payload.client = formValues.client;
        payload.create_as_admin = false;
      } else {
        payload.create_as_admin = true;
      }

      zonesService
        .create(payload)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("ZONE_CREATED_SUCCESSFULLY"),
          });
          navigate("/openstack/zones");
        })
        .catch((error) => {
          const backendErrors = error?.response?.data;
          if (backendErrors?.detail) {
            viewAlert({
              severity: "error",
              message: backendErrors.detail,
            });
          } else if (backendErrors) {
            setErrors(backendErrors);
          } else {
            viewAlert({
              severity: "error",
              message: t("FAILED_TO_CREATE_ZONE"),
            });
          }
        })
        .finally(() => {
          setCreateLoading(false);
          setPageLoading(false);
        });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setErrors,
  } = formik;

  const onFormSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      formik.submitForm();
    },
    [formik]
  );

  // Fetch price estimation when creating in client project
  useEffect(() => {
    if (createInClientProject && values.client && values.email && values.ttl) {
      setPriceLoading(true);
      const timer = setTimeout(() => {
        zonesService
          .getPrice(values.email, values.ttl, values.client)
          .then((res) => {
            setEstimatedPrice(res.data?.estimated_cost_display || "");
          })
          .catch((error) => {
            console.error("Failed to fetch price", error);
            setEstimatedPrice("");
          })
          .finally(() => {
            setPriceLoading(false);
          });
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setEstimatedPrice("");
    }
  }, [createInClientProject, values.client, values.email, values.ttl]);

  if (optionsLoading || !createOptions) {
    return (
      <Container>
        <TopProgress loading={true} />
        <Typography variant="body">{t("OPTIONS_LOADING")}</Typography>
      </Container>
    );
  }

  const formikProps = {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
  };

  return (
    <Container>
      <TopProgress loading={pageLoading} />
      <Header>
        <BackArrow fallbackPath="/openstack/zones" />
        <Typography variant="h1">{t("CREATE_DNS_ZONE")}</Typography>
      </Header>
      <FormSection as="form" onSubmit={onFormSubmit}>
        <RadioGroup>
          <RadioOption>
            <input
              type="radio"
              name="projectType"
              checked={!createInClientProject}
              onChange={() => setCreateInClientProject(false)}
            />
            <RadioLabel>{t("CREATE_ZONE_IN_ADMIN_PROJECT")}</RadioLabel>
          </RadioOption>
          <RadioOption>
            <input
              type="radio"
              name="projectType"
              checked={createInClientProject}
              onChange={() => setCreateInClientProject(true)}
            />
            <RadioLabel>{t("CREATE_ZONE_IN_CLIENT_PROJECT")}</RadioLabel>
          </RadioOption>
        </RadioGroup>

        {createInClientProject && (
          <Select
            required
            title={t("CLIENT")}
            data={clientOptions}
            selectedValue={values.client}
            onChange={(value: string) => setFieldValue("client", value)}
            placeholder={t("CHOOSE_CLIENT")}
            fieldName="client"
            hasSearch={true}
            onSearchChange={setClientSearchString}
            {...formikProps}
          />
        )}

        <Select
          required
          title={t("ZONE_TYPE")}
          data={typeOptions}
          selectedValue={values.type_}
          onChange={(value: string) => setFieldValue("type_", value)}
          placeholder={t("CHOOSE")}
          fieldName="type_"
          {...formikProps}
        />

        <Input
          title={t("IP_ADDRESS")}
          placeholder=""
          fieldName="ip_address"
          {...formikProps}
        />

        <Input
          required
          title={t("DOMAIN_NAME")}
          placeholder="example.com"
          fieldName="name"
          {...formikProps}
        />

        <Input
          required
          title={t("EMAIL")}
          placeholder="admin@example.com"
          fieldName="email"
          type="email"
          {...formikProps}
        />

        <Input
          title={t("DESCRIPTION")}
          placeholder=""
          fieldName="description"
          {...formikProps}
        />

        <Input
          required
          title={t("TTL")}
          placeholder="1800"
          fieldName="ttl"
          type="number"
          {...formikProps}
        />

        {estimatedPrice && (
          <PriceInfo>
            <Typography variant="body">
              {t("ESTIMATED_COST")}: <strong>{estimatedPrice}</strong>
            </Typography>
          </PriceInfo>
        )}

        <FormActions>
          <Button
            variant="stroke"
            size="display"
            title={t("CANCEL")}
            onClick={() => navigate("/openstack/zones")}
          />
          <Button
            variant="primary"
            size="display"
            title={t("CREATE")}
            loading={createLoading}
            onClick={onFormSubmit}
          />
        </FormActions>
      </FormSection>
    </Container>
  );
};

export default ZoneCreate;
